import type { KeyboardEvent, ChangeEvent, RefObject } from 'react';
import React, { Component, createRef, useContext } from 'react';
import type { WrappedComponentProps } from 'react-intl-next';
import { defineMessages, injectIntl } from 'react-intl-next';
import debounce from 'lodash/debounce';
import memoize from 'lodash/memoize';

import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next';
import { withAnalyticsEvents } from '@atlaskit/analytics-next';
import { Box, xcss } from '@atlaskit/primitives';
import { SpotlightManager, SpotlightTarget, SpotlightTransition } from '@atlaskit/onboarding';

import { eventHub } from '@atlassian/editor-plugin-ai/EventHub';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import { AIBorder } from '@atlassian/ai-components';

import { ALLOW_SHORTCUTS_CLASS, isMac } from '@confluence/shortcuts';
import { KeyboardEventKeys } from '@confluence/keyboard-events';
import type { FlagDescriptor, FlagsStateContainer } from '@confluence/flags';
import { withFlags } from '@confluence/flags';
import { ALLOWED_FLAG_MESSAGES, buildEditorFlagMessage } from '@confluence/editor-flags';
import { expValEquals, expValEqualsNoExposure } from '@confluence/feature-experiments';
import type { ExperienceTrackerAPI } from '@confluence/experience-tracker';
import { AI_TITLE_GENERATION, ExperienceTrackerContext } from '@confluence/experience-tracker';
import type { FullPageEditorPresetAPI } from '@confluence/editor-presets/entry-points/useFullPageEditorPreset';
import { withIsAIEnabledForContent } from '@confluence/ai-common/entry-points/useIsAIEnabledForContent';
import {
	RedactionsTourSteps,
	RedactionTourStepSource,
	REDACTIONS_TOUR_TARGETS,
	useRedactionsTour,
	useTrackTitleRedactionRendered,
} from '@confluence/redactions/entry-points/tour';

import {
	EditorTitleTextArea,
	AIGeneratedEditorTitleTextArea,
	EditorTitleTextAreaNext,
} from './editorTitleStyled';
import { EditorTitleContainer } from './shared-components/EditorTitleContainer';
import { SSRTitleScript } from './SSRTitleScript';

// The duration is hard-coded in styled bc compiled css will not allow us to insert this timing dynamically. It's also slightly longer to account for easing out
const ERROR_ANIMATION_DURATION = 1200;
export const MAX_LENGTH_OF_TITLE = 255;
export const onChangeTitleDebounceTime = 400;

const i18n = defineMessages({
	blogpostTitlePlaceholder: {
		id: 'editor-title.existing.blogpost.placeholder',
		defaultMessage: 'Blog post title',
		description: 'Placeholder for Blog post title text area',
	},
	editorOnboardingExpTitlePlaceholderText: {
		id: 'editor-title.editor.onboarding.title.placeholder',
		defaultMessage: 'Give this page a title',
		description: 'Placeholder for editor onboarding title textarea',
	},
	aiGeneratedTitleDismissButton: {
		id: 'editor-title.ai.title.error.dismiss',
		defaultMessage: 'Dismiss',
		description:
			'The button dismisses the flag shown when an error occurred trying to generate a title with AI',
	},
});

/*
  Adding this is necessary as the AI suggest title feature works by checking for the presence of this custom data attribute
*/
export const AI_SUGGEST_TITLE_PROPS = {
	'data-ai-suggest-title-target': 'true',
};

export type EditorTitleProps = {
	contentId: string;
	title: string | null;
	onChange: (value: string) => void;
	onFocusChanged: (focusChanged: boolean) => void;
	onTitleBlur: () => void;
	focusEditor: () => void;
	setGeneratedTitle: (value: string) => void | undefined;
	setTrackAIResultViewStored: (attribute: any) => void | undefined;
	trackAIInteractionInit: () => void;
	trackAIResultView: (attribute: any) => void;
	trackAIResultError: (attribute: any) => void;
	activeRedactionsSpotlight: React.JSX.Element | null;
	experienceTracker: ExperienceTrackerAPI;
	isAIEnabledForContent: boolean;
	isDisabled?: boolean;
	debounceOnChange?: boolean;
	contentType: 'page' | 'blogpost' | null;
	userOffline?: boolean;
	isInPreview?: boolean;
	isTemplateEditor?: boolean;
	templateSuggestionsRef?: RefObject<HTMLDivElement> | null;
	templateButtonRef?: RefObject<HTMLElement> | null;
	isTemplatesSuggestionsEnabled?: boolean;
	flagArray?: Array<FlagDescriptor>;
	flags: FlagsStateContainer;
	isTitleAIGenerated?: boolean;
	isTitleCenterAligned?: boolean;
	isUnpublishedDraft?: boolean;
	isEditMode?: boolean;
	spaceKey: string;
	spaceId?: string;
	editorApi?: FullPageEditorPresetAPI;
} & WrappedComponentProps &
	WithAnalyticsEventsProps;

type EditorTitleState = {
	title: string | null;
	prevTitle: string | null;
	shouldFocusTitle: boolean;
	focusChanged: boolean;
	shouldAnimateTitle: boolean;
	isTitleAIGenerated: boolean;
	isTitleGenerating: boolean;
};

const AIBorderStyles = xcss({
	backgroundColor: 'elevation.surface',
});

const PlaceholderStyles = xcss({
	// Necessary for proper editor content/title alignment
	marginTop: 'space.negative.075',
	marginBottom: 'space.050',
});

const TitleGenerationPlaceholder = () => {
	return (
		<>
			<Box padding="space.200" />
			<Box padding="space.025" />
		</>
	);
};

export const fieldSizingEnabled = () => {
	// we manually expose here: next/packages/editor-title/src/editorTitleStyled.tsx line ~160
	if (!expValEquals('platform_editor_editor_title_field_sizing', 'variant', 'test')) {
		return false;
	}

	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		return true;
	}

	return CSS.supports('field-sizing', 'content');
};

/**
 * @todo CTE-268 typing in title is slow with in-editor templates (long term solution)
 * For now, we are solving this by debouncing calls to the onChange prop. Once the long term
 * solution is ready (see ticket for details), we should remove the debounceOnChange prop and logic
 */
class EditorTitleComponentClass extends Component<EditorTitleProps, EditorTitleState> {
	_textareaRef: RefObject<HTMLTextAreaElement>;
	_hasEditEventFiredRef: React.MutableRefObject<boolean>;

	static getDerivedStateFromProps(props: EditorTitleProps, state: EditorTitleState) {
		if (props.title !== state.prevTitle) {
			return {
				title: props.title,
				prevTitle: props.title,
			};
		}

		return null;
	}

	constructor(props: EditorTitleProps) {
		super(props);

		this._textareaRef = createRef<HTMLTextAreaElement>();
		this._hasEditEventFiredRef = { current: false };
	}

	state = {
		title: this.props.title,
		// eslint-disable-next-line react/no-unused-state
		prevTitle: this.props.title,
		shouldFocusTitle: true,
		focusChanged: false,
		shouldAnimateTitle: false,
		isTitleAIGenerated: false,
		isTitleGenerating: false,
	};

	componentDidMount() {
		const { isTemplateEditor, title } = this.props;
		const { shouldFocusTitle } = this.state;
		const shouldTemplateEditorTitleFocus = isTemplateEditor && !title && shouldFocusTitle;

		if (shouldTemplateEditorTitleFocus) {
			this._focusTextarea();
		}

		this._updateHeightAuto();
		window.addEventListener('resize', this._onWindowResize);
	}

	componentDidUpdate(prevProps: EditorTitleProps, prevState: EditorTitleState) {
		const { userOffline, focusEditor, title, isInPreview, flagArray } = this.props;
		const { focusChanged, shouldFocusTitle } = this.state;
		const userWasOffline = prevProps.userOffline && !userOffline;
		const userWasNotOffline = !prevProps.userOffline && !userOffline;

		this._updateHeightAuto();
		if (prevState.prevTitle !== title) {
			this._debouncedOnChange.cancel();
		}
		// if user was offline
		if (userWasOffline) {
			focusChanged && shouldFocusTitle ? this._focusTextarea() : focusEditor();
		}
		// if user was not offline
		if (userWasNotOffline && !title && shouldFocusTitle) {
			this._focusTextarea();
		}

		/** Focus title if title-related error flag:
		 * We check the flags array length against that of the flags from prevProps so that
		 * we can detect if a new error flag has been added. If the current length is greater
		 * than the previous length, a flag was added.
		 */
		if (
			flagArray &&
			prevProps.flagArray &&
			flagArray.length > prevProps.flagArray.length &&
			this._hasTitleError()
		) {
			this._onTitleError();
		}

		if (prevProps.title === title && prevProps.isInPreview === isInPreview) {
			return;
		}
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this._onWindowResize); // cleanup event listener
		this._debouncedOnChange.flush();
	}

	/**
	 * Checks if the flags array has a title-relevant error
	 * 1. empty title / title with only whitespace
	 * 2. duplicate title
	 */
	_hasTitleError = memoize(() => {
		const { flagArray, intl } = this.props;

		// These flag messages are always defined so they shouldn't return null
		const titleErrors = [
			buildEditorFlagMessage({
				errorData: { [ALLOWED_FLAG_MESSAGES.INVALID_PAGE_TITLE]: true },
				formatMessage: intl.formatMessage,
			})?.title,
			buildEditorFlagMessage({
				errorData: { [ALLOWED_FLAG_MESSAGES.DUPLICATE_TITLE]: true },
				formatMessage: intl.formatMessage,
			})?.title,
			buildEditorFlagMessage({
				errorData: { [ALLOWED_FLAG_MESSAGES.TITLE_REQUIRED]: true },
				formatMessage: intl.formatMessage,
			})?.title,
			buildEditorFlagMessage({
				errorData: { [ALLOWED_FLAG_MESSAGES.AI_GENERATED_TITLE]: true },
				formatMessage: intl.formatMessage,
			})?.title,
			buildEditorFlagMessage({
				errorData: {
					[ALLOWED_FLAG_MESSAGES.DUPLICATE_TITLE_WITHIN_EDITOR]: true,
				},
				formatMessage: intl.formatMessage,
			})?.title,
		];

		if (flagArray && flagArray.length > 0) {
			// We only want to check the most recent, incoming error in case user hasn't dismissed a previous error
			const errorMessage = String(flagArray[0].title);
			return titleErrors.includes(errorMessage);
		}
		return false;
	});

	_focusTextarea() {
		if (this._textareaRef.current) {
			this._textareaRef.current.focus();
		}
	}

	_debouncedOnChange = debounce((value) => {
		this.props.onChange(value);
	}, onChangeTitleDebounceTime);

	_onTitleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
		const { createAnalyticsEvent, contentId, spaceId } = this.props;
		const newTitle = event.target.value;
		this.setState({ title: newTitle, isTitleAIGenerated: false });

		if (this.props.debounceOnChange) {
			this._debouncedOnChange(newTitle);
		} else {
			this.props.onChange(newTitle);
		}

		if (!this._hasEditEventFiredRef.current) {
			createAnalyticsEvent?.({
				type: 'sendTrackEvent',
				data: {
					source: 'editorTitle',
					action: 'edited',
					actionSubject: 'editor',
					objectId: contentId,
					objectType: 'page',
					attributes: { isLivePage: false },
					containerType: 'space',
					containerId: spaceId,
				},
			}).fire();
			this._hasEditEventFiredRef.current = true;
		}
	};

	_onFocusChanged = (focusChanged: boolean) => {
		this.setState({
			shouldFocusTitle: focusChanged,
			focusChanged: true,
		});
		this.props.onFocusChanged(focusChanged);
	};

	_setTitleAI = (newTitle: string) => {
		this.setState({ isTitleGenerating: false, title: newTitle, isTitleAIGenerated: true });
		this.props.onChange(newTitle);
	};

	generateTitle = () => {
		const {
			trackAIResultError,
			trackAIResultView,
			trackAIInteractionInit,
			setGeneratedTitle,
			setTrackAIResultViewStored,
			experienceTracker,
			flags,
			intl,
		} = this.props;

		const filteredProseMirrors = Array.from(document.querySelectorAll('.ProseMirror')).filter(
			(el) => !el.closest('[data-testid="chromeless-editor"]'),
		);
		const targetEditorId = (filteredProseMirrors[0] as HTMLElement)?.dataset?.editorId;

		let generatedTitle = '';

		if (targetEditorId) {
			trackAIInteractionInit();
			setTrackAIResultViewStored?.(trackAIResultView);
			experienceTracker.start({
				name: AI_TITLE_GENERATION,
			});
			eventHub.publishToEditor({
				event: 'start prompt',
				data: {
					targetEditorId,
					analyticSourceId: 'confluenceTitleGenOnPublish',
					prompt: 'suggest title',
					hideModal: true,
					streamingCallback: (data) => {
						switch (data.type) {
							case 'initialising':
								this.setState({ isTitleGenerating: true });
								break;
							case 'streaming':
								generatedTitle = data.result.replace(/\n$/, '');
								this._setTitleAI(generatedTitle);
								break;
							case 'complete':
								generatedTitle = data.result.replace(/\n$/, '');
								this._setTitleAI(generatedTitle);
								trackAIResultView({ doesNotMeetMAUCriteria: true });
								experienceTracker.succeed({
									name: AI_TITLE_GENERATION,
								});
								setGeneratedTitle?.(generatedTitle);
								break;
							case 'error':
								trackAIResultError({
									aiErrorMessage: data.errorInfo?.errorContent || undefined,
									aiErrorCode: data.errorInfo?.statusCode || 500,
								});
								experienceTracker.fail({
									name: AI_TITLE_GENERATION,
									error: new Error(data.errorInfo?.errorContent || undefined),
								});

								this.setState({ isTitleGenerating: false });

								const errorFlagMessage = buildEditorFlagMessage({
									errorData: { [ALLOWED_FLAG_MESSAGES.AI_GENERATED_TITLE_ERROR]: true },
									formatMessage: intl.formatMessage,
								});
								flags.hideFlag('ai-generated-title-flag');
								flags.showErrorFlag({
									id: 'ai-generated-title-error-flag',
									title: errorFlagMessage?.title,
									description: errorFlagMessage?.description as React.ReactNode,
									actions: [
										{
											content: intl.formatMessage(i18n.aiGeneratedTitleDismissButton),
											onClick: () => flags.hideFlag('ai-generated-title-error-flag'),
										},
									],
								});
								break;
						}
					},
				},
			});
		}
	};

	_onTitleError = () => {
		const { createAnalyticsEvent, flagArray, intl, contentId, spaceId, isAIEnabledForContent } =
			this.props;

		const flagTitle = String(flagArray?.[0]?.title);

		const titleRequiredFlagTitle = buildEditorFlagMessage({
			errorData: { [ALLOWED_FLAG_MESSAGES.TITLE_REQUIRED]: true },
			formatMessage: intl.formatMessage,
		})?.title;

		const aiGeneratedTitleFlagTitle = buildEditorFlagMessage({
			errorData: { [ALLOWED_FLAG_MESSAGES.AI_GENERATED_TITLE]: true },
			formatMessage: intl.formatMessage,
		})?.title;

		if (
			flagArray &&
			(flagTitle === titleRequiredFlagTitle || flagTitle === aiGeneratedTitleFlagTitle)
		) {
			createAnalyticsEvent?.({
				type: 'sendTrackEvent',
				data: {
					source: 'editorTitle',
					action: 'publishAttemptedEmptyTitle',
					actionSubject: 'editor',
					objectId: contentId,
					objectType: 'page',
					attributes: {
						isLivePage: false,
						isAiTitleEnabled:
							String(flagArray?.[0]?.title) ===
							buildEditorFlagMessage({
								errorData: { [ALLOWED_FLAG_MESSAGES.AI_GENERATED_TITLE]: true },
								formatMessage: intl.formatMessage,
							})?.title,
					},
					containerType: 'space',
					containerId: spaceId,
				},
			}).fire();
		}

		if (
			flagArray &&
			String(flagArray?.[0]?.title) ===
				buildEditorFlagMessage({
					errorData: { [ALLOWED_FLAG_MESSAGES.AI_GENERATED_TITLE]: true },
					formatMessage: intl.formatMessage,
				})?.title &&
			isAIEnabledForContent &&
			expValEquals('confluence_ai_title_generation', 'cohort', 'test')
		) {
			this._focusTextarea();
			this.generateTitle();
		} else {
			this._focusTextarea();

			this.setState({ shouldAnimateTitle: true });
			/*
			 * Additional focus needed after animation bc setting the shouldAnimateTitle state will change the key of the textArea,
			 * which means re-render will regenerate entire view, causing loss of focus
			 */
			setTimeout(() => {
				this.setState({ shouldAnimateTitle: false });
				this._focusTextarea();
			}, ERROR_ANIMATION_DURATION);
		}
	};

	_updateHeightAuto() {
		// if the browser doesn't support `field-sizing: content`,
		// this needs to run to set the height of the textarea
		if (!fieldSizingEnabled()) {
			if (this._textareaRef.current) {
				// this line will make a reflow so that `textArea.scrollHeight` is updated with correct value.
				this._textareaRef.current.style.height = 'auto';
				// In SSR environment, the previous line does not cause a reflow, so the `scrollHeight` stays zero
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				if (!process.env.REACT_SSR) {
					this._textareaRef.current.style.height = `${this._textareaRef.current.scrollHeight}px`;
				}
			}
		}
	}

	_onFocus = () => {
		this._onFocusChanged(true);
	};

	_onBlur = () => {
		this.props.onTitleBlur();
		this._onFocusChanged(false);
	};

	/**
	 * Triggered from window resize event listener
	 */
	_onWindowResize = debounce(() => {
		this._updateHeightAuto();
	}, 200);

	_onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
		// If the user is composing characters using, e.g., the Hiragana - Kana keyboard,
		// short circuit the code to prevent the bug in Chrome where the selected text
		// gets copied into the editor body.
		if (event.nativeEvent.isComposing) {
			return;
		}

		// Shift+Tab should not be prevented, so we check if tab pressed without shift
		const isTabPressed = event.key === KeyboardEventKeys.TAB.key && !event.shiftKey;
		// If enter is pressed without publish keyboard shortcut prompt key, prevent default and insert newline in editor body
		const promptKeyForPublishShortcut = isMac() ? event.metaKey : event.ctrlKey;
		const isEnterPressed =
			event.key === KeyboardEventKeys.ENTER.key && !promptKeyForPublishShortcut;
		const isDownArrowPressed = event.key === KeyboardEventKeys.DOWN.key;

		if (isTabPressed || isEnterPressed || isDownArrowPressed) {
			event.preventDefault();
			event.stopPropagation();
			if (this.props.isTemplatesSuggestionsEnabled && this.props.templateButtonRef?.current) {
				this.props.templateSuggestionsRef?.current?.focus();
			} else {
				const { editorApi } = this.props;
				if (isEnterPressed) {
					editorApi?.core?.actions.execute(
						editorApi?.pageTitleUtils?.commands?.insertNewLineAtDocStart(),
					);
				}
				if (isTabPressed || isDownArrowPressed) {
					editorApi?.core?.actions.execute(editorApi?.pageTitleUtils?.commands?.selectDocStart());
				}
				this.props.focusEditor();
			}
		}
	};

	render() {
		const {
			intl: { formatMessage },
			isDisabled,
			contentType,
			isTitleCenterAligned,
			isAIEnabledForContent,
		} = this.props;

		// We store local changes in state when we debounce calls to this.props.onChange.
		// So state contains the most updated title value in this case
		const title = this.props.debounceOnChange ? this.state.title : this.props.title;

		const placeholder =
			contentType === 'blogpost'
				? formatMessage(i18n.blogpostTitlePlaceholder)
				: formatMessage(i18n.editorOnboardingExpTitlePlaceholderText);

		const UseEditorTextAreaNext = fieldSizingEnabled()
			? EditorTitleTextAreaNext
			: EditorTitleTextArea;

		const EditorTitleTextAreaToUse =
			isAIEnabledForContent &&
			expValEqualsNoExposure('confluence_ai_title_generation', 'cohort', 'test')
				? AIGeneratedEditorTitleTextArea
				: UseEditorTextAreaNext;

		if (this.state.isTitleGenerating) {
			return (
				<Box xcss={PlaceholderStyles}>
					<AIBorder loading useModalStyles hideGlow customInnerContainerStyles={AIBorderStyles}>
						<TitleGenerationPlaceholder />
					</AIBorder>
				</Box>
			);
		}

		return (
			<SpotlightManager>
				<SpotlightTarget name={REDACTIONS_TOUR_TARGETS.TITLE_REDACTION}>
					<EditorTitleContainer>
						<EditorTitleTextAreaToUse
							name="editpages-title"
							data-test-id="editor-title"
							{...AI_SUGGEST_TITLE_PROPS}
							maxLength={MAX_LENGTH_OF_TITLE}
							aria-label={placeholder}
							placeholder={placeholder}
							// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
							className={ALLOW_SHORTCUTS_CLASS}
							value={title || ''}
							required
							rows={1}
							ref={this._textareaRef}
							onKeyDown={this._onKeyDown}
							onChange={this._onTitleChange}
							onFocus={this._onFocus}
							onBlur={this._onBlur}
							data-gramm={false}
							disabled={isDisabled}
							shouldAnimate={this.state.shouldAnimateTitle}
							isTitleAIGenerated={this.state.isTitleAIGenerated}
							isTitleCenterAligned={isTitleCenterAligned}
							key={this.state.shouldAnimateTitle ? 'editor-title-animated' : 'editor-title'} // This key swapping is necessary to trigger and reset the title animation
						/>
						<SSRTitleScript selector="textarea[name=editpages-title]" />
					</EditorTitleContainer>
				</SpotlightTarget>
				<SpotlightTransition>{this.props.activeRedactionsSpotlight ?? null}</SpotlightTransition>
			</SpotlightManager>
		);
	}
}

function aiAnalyticsEvents(WrappedComponent) {
	return function WithAIAnalytics(props) {
		const { trackAIInteractionInit, trackAIResultError, trackAIResultView } =
			useAIEventsInstrumentation();

		return (
			<WrappedComponent
				{...props}
				trackAIInteractionInit={trackAIInteractionInit}
				trackAIResultError={trackAIResultError}
				trackAIResultView={trackAIResultView}
			/>
		);
	};
}

const redactionsChangeboarding = (WrappedComponent) => (props) => {
	const { contentId, title } = props;
	useTrackTitleRedactionRendered({ contentId, title });
	const { activeSpotlight } = useRedactionsTour({
		contentId,
		step: RedactionsTourSteps.FIRST,
		firstStepSource: RedactionTourStepSource.titleRedaction,
	});
	return <WrappedComponent {...props} activeRedactionsSpotlight={activeSpotlight} />;
};

const experienceTracking = (WrappedComponent) => (props) => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	return <WrappedComponent {...props} experienceTracker={experienceTracker} />;
};

export const EditorTitle =
	//Adding HOC since flagArray is needed to trigger componentDidUpdate
	withFlags(
		withIsAIEnabledForContent(
			injectIntl(
				experienceTracking(
					redactionsChangeboarding(
						aiAnalyticsEvents(withAnalyticsEvents()(EditorTitleComponentClass)),
					),
				),
			),
		),
	);
